// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the board.
//
// ── THE RULE THE WHOLE LAYOUT HANGS ON ───────────────────────────────────────────────────────────
//
// **The action bar is the only place an action ever appears, and it never moves.** Every verb —
// Raise, Douse, Point — is "tap the thing you mean, then tap the verb in the bar at the bottom".
// Two taps for everything, and the second tap is always in the same 56px strip under your thumb.
//
// That is not a style preference, it is what makes the game fit on a phone at all. At five players
// with six suits there are up to twenty teammate cards, four of your own, six plumes and thirty
// census marks on screen. If any of those regions were also interactive, each would need 44px
// targets and its own affordances and the screen would be gone. Because exactly one region is
// mutable-interactive, every other region can be pure display at whatever density it needs — and a
// teammate card can be 46px wide, because you are never asked to hit it precisely.
//
// One gesture exists besides tapping: swipe DOWN on your own card to douse it. The governing rule is
// written here so it cannot be forgotten: **a gesture may only be bound to an action that cannot
// lose the game on a single mis-input.** Dousing qualifies (it costs a card and gives a spark back);
// raising does not, so raising has no gesture and never will.

import { makeDraggable } from '@ben-gy/game-engine/drag';
import { TOP, rankOf, suitOf } from './cards';
import { fillFor } from './palette';
import {
  handView,
  plumeViews,
  previewSlots,
  seatViews,
  type CensusCell,
  type MateCard,
  type SlotView,
} from './view';
import { HIDDEN, K_COLOUR, K_RANK, MAX_HAZE, MAX_SPARKS, hintMask, legal, packDouse, packPoint, packRaise, type Game } from './game';

export type Selection =
  | { kind: 'none' }
  /** One of your own cards is picked; the bar offers Raise and Douse. */
  | { kind: 'mine'; slot: number }
  /** A teammate is picked; the bar becomes the colour/rank picker. */
  | { kind: 'mate'; seat: number }
  /** A colour or rank is chosen for that teammate; the bar offers Point. */
  | { kind: 'aim'; seat: number; hintKind: number; value: number };

export interface TableDeps {
  submit: (action: number) => void;
  onSelect: (sel: Selection) => void;
  sfx: (name: string) => void;
  onQuit: () => void;
}

export interface TableState {
  g: Game;
  you: number | null;
  names: string[];
  away: Set<number>;
  bots: Set<number>;
  sel: Selection;
  /** The seat holding the deck, named out loud rather than hidden. */
  dealerSeat: number | null;
  /** Set while this client is waiting for the dealer to accept an action. */
  pending: boolean;
  sixth: 'prism' | 'onyx' | null;
}

const el = (tag: string, cls?: string, text?: string): HTMLElement => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};

/** Always a real <button>: keyboard-focusable, announced as a control, and typed as one. */
const btn = (cls: string, text?: string): HTMLButtonElement => {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  if (text !== undefined) b.textContent = text;
  return b;
};

const CENSUS_GLYPH: Record<CensusCell, string> = {
  raised: '▪',
  spare: '○',
  last: '◉',
  lost: '╱',
};
const CENSUS_LABEL: Record<CensusCell, string> = {
  raised: 'raised',
  spare: 'spare copies left',
  last: 'last copy — do not douse',
  lost: 'all copies gone',
};

export class Table {
  readonly root: HTMLElement;
  private board = el('div', 'board');
  private plumeRow = el('div', 'plumes');
  private counters = el('div', 'counters');
  private seatsEl = el('div', 'seats');
  private ticker = el('p', 'ticker');
  private handEl = el('div', 'hand');
  private bar = el('div', 'actionbar');
  private quitBtn = btn('quit', 'Quit');
  private state: TableState | null = null;
  private deps: TableDeps;
  private drags: Array<{ destroy: () => void }> = [];

  constructor(deps: TableDeps) {
    this.deps = deps;
    this.root = el('div', 'table-screen');
    this.ticker.setAttribute('aria-live', 'polite');
    this.plumeRow.setAttribute('aria-label', 'The plumes, and what is left of each colour');
    this.bar.setAttribute('aria-label', 'Actions');
    this.quitBtn.addEventListener('click', () => deps.onQuit());
    this.board.append(this.plumeRow, this.counters, this.seatsEl, this.ticker);
    const dock = el('div', 'dock');
    dock.append(this.handEl, this.bar);
    this.root.append(this.board, dock);
  }

  say(text: string): void {
    this.ticker.textContent = text;
  }

  render(state: TableState): void {
    this.state = state;
    this.renderPlumes();
    this.renderCounters();
    this.renderSeats();
    this.renderHand();
    this.renderBar();
  }

  // ── the plume rack, which is also the census and also the legend ───────────────────────────────

  private renderPlumes(): void {
    const s = this.state!;
    const views = plumeViews(s.g);
    this.plumeRow.replaceChildren();
    this.plumeRow.style.setProperty('--plume-count', String(views.length));
    for (const p of views) {
      const col = el('div', `plume${p.capped ? ' capped' : ''}`);
      col.style.setProperty('--suit', fillFor(p.suit, s.sixth));
      const head = el('div', 'plume-head');
      head.append(el('span', 'plume-glyph', p.glyph));
      col.append(head);
      col.append(el('div', 'plume-height', String(p.height)));
      const ladder = el('div', 'ladder');
      // Rank 5 at the top, so the column reads upward the way the plume grows.
      for (let r = TOP; r >= 1; r--) {
        const cell = p.ladder[r - 1];
        const dot = el('span', `cen cen-${cell}`, CENSUS_GLYPH[cell]);
        dot.title = `${p.name} ${r}: ${CENSUS_LABEL[cell]}`;
        ladder.append(dot);
      }
      col.append(ladder);
      col.setAttribute(
        'aria-label',
        `${p.name}: raised to ${p.height}. ${p.ladder
          .map((c, i) => `${i + 1} ${CENSUS_LABEL[c]}`)
          .join(', ')}`,
      );
      this.plumeRow.append(col);
    }
  }

  private renderCounters(): void {
    const s = this.state!;
    this.counters.replaceChildren();
    const sparks = el('div', 'ctr sparks');
    // Dots, not a number. You have to FEEL the pool shrinking; a digit has to be read.
    for (let i = 0; i < MAX_SPARKS; i++) sparks.append(el('span', `pip${i < s.g.sparks ? ' on' : ''}`, '●'));
    sparks.setAttribute('aria-label', `${s.g.sparks} of ${MAX_SPARKS} sparks left`);
    const haze = el('div', 'ctr haze');
    for (let i = 0; i < MAX_HAZE; i++) haze.append(el('span', `lamp${i < s.g.haze ? ' out' : ''}`, '▲'));
    haze.setAttribute('aria-label', `${MAX_HAZE - s.g.haze} of ${MAX_HAZE} clear sky left`);
    const deck = el('div', 'ctr deck', `${s.g.deckSize - s.g.drawn} left`);
    /**
     * Quit lives HERE, in the counters row, and not floated over the board.
     *
     * It was `position: absolute; top: 6px; right: 8px` for exactly one build, and at 375px it sat
     * squarely on top of the fifth plume — hiding that colour's height and half its census ladder on
     * every screen of every mode. Nothing caught it but looking: jsdom has no layout engine, and the
     * overlap is invisible at desktop width where the rack is wider than the button. In the flow it
     * cannot overlap anything by construction, which is the fix rather than a smaller offset.
     */
    this.counters.append(sparks, haze, deck, this.quitBtn);
  }

  // ── everybody else's hands ─────────────────────────────────────────────────────────────────────

  private renderSeats(): void {
    const s = this.state!;
    const views = seatViews(s.g, s.you, s.names, s.away, s.bots);
    this.seatsEl.replaceChildren();
    const aiming = s.sel.kind === 'aim' ? s.sel : null;

    for (const seat of views) {
      if (seat.you) continue; // your own hand lives in the dock, and it is not a hand of cards
      const row = el('div', `seatrow${seat.active ? ' active' : ''}${seat.away ? ' away' : ''}`);
      const name = btn('seatname');
      name.append(el('span', 'seatname-text', seat.name));
      const tags: string[] = [];
      if (seat.away) tags.push('covered');
      else if (seat.bot) tags.push('crew');
      if (s.dealerSeat === seat.seat) tags.push('holds the deck');
      if (tags.length) name.append(el('span', 'seattag', tags.join(' · ')));
      name.setAttribute('aria-label', `Point something out to ${seat.name}`);
      const canPoint = s.you !== null && s.g.turn === s.you && s.g.sparks > 0 && !s.g.over;
      name.disabled = !canPoint;
      name.addEventListener('click', () => {
        this.deps.sfx('tap');
        this.deps.onSelect({ kind: 'mate', seat: seat.seat });
      });
      row.append(name);

      const cards = el('div', 'matecards');
      const lit = aiming && aiming.seat === seat.seat
        ? previewSlots(s.g, seat.seat, aiming.hintKind, aiming.value)
        : null;
      for (const c of seat.cards) cards.append(this.mateCard(c, lit));
      row.append(cards);
      this.seatsEl.append(row);
    }
  }

  private mateCard(c: MateCard, lit: number[] | null): HTMLElement {
    const s = this.state!;
    const card = el('div', 'matecard');
    if (c.card === HIDDEN) {
      card.classList.add('unknown');
      card.append(el('span', 'rank', '?'));
      return card;
    }
    card.style.setProperty('--suit', fillFor(suitOf(c.card), s.sixth));
    card.append(el('span', 'rank', String(rankOf(c.card))));
    card.append(el('span', 'glyph', s.g.suits.find((x) => x.id === suitOf(c.card))?.glyph ?? '?'));
    if (c.toldSuit) card.classList.add('told-suit');
    if (c.toldRank) card.classList.add('told-rank');
    if (c.intent === 'play') card.classList.add('flag-play');
    if (c.intent === 'save') card.classList.add('flag-save');
    if (c.isChop) card.classList.add('chop');
    if (lit) card.classList.add(lit.includes(c.slot) ? 'lit' : 'unlit');
    const bits = [
      `${s.g.suits.find((x) => x.id === suitOf(c.card))?.name} ${rankOf(c.card)}`,
      c.intent === 'play' ? 'told to raise it' : c.intent === 'save' ? 'told to keep it' : '',
      c.isChop ? 'next to be doused' : '',
    ].filter(Boolean);
    card.setAttribute('aria-label', bits.join(', '));
    return card;
  }

  // ── your own hand: five things you do not know ─────────────────────────────────────────────────

  private renderHand(): void {
    const s = this.state!;
    for (const d of this.drags) d.destroy();
    this.drags = [];
    this.handEl.replaceChildren();
    if (s.you === null) {
      this.handEl.append(el('p', 'watching', 'Watching this round. You are in the next one.'));
      return;
    }
    const slots = handView(s.g, s.you);
    this.handEl.style.setProperty('--slot-count', String(slots.length));
    for (const v of slots) this.handEl.append(this.ownSlot(v));
  }

  private ownSlot(v: SlotView): HTMLElement {
    const s = this.state!;
    const picked = s.sel.kind === 'mine' && s.sel.slot === v.slot;
    const slot = btn(`slot v-${v.verdict}${picked ? ' picked' : ''}${v.isChop ? ' chop' : ''}`);
    if (v.intent) slot.classList.add(`flag-${v.intent}`);

    // What it could still be: a rail of suit chips and a rail of rank digits. Position is the
    // identity in both, so "still possible" can be a fill and "ruled out" an outline — which reads
    // identically with no colour vision at all.
    const chips = el('div', 'chips');
    for (const suit of s.g.suits) {
      const on = v.suits.includes(suit.id);
      const chip = el('span', `chip${on ? ' on' : ''}`, suit.glyph);
      if (on) chip.style.setProperty('--suit', fillFor(suit.id, s.sixth));
      chips.append(chip);
    }
    const digits = el('div', 'digits');
    for (let r = 1; r <= TOP; r++) digits.append(el('span', `digit${v.ranks.includes(r) ? ' on' : ''}`, String(r)));

    const badge = el('span', 'verdict');
    badge.textContent =
      v.intent === 'play' ? 'RAISE' : v.intent === 'save' ? 'KEEP' : v.verdict === 'go' ? 'RAISE' : v.verdict === 'spent' ? 'SPENT' : v.verdict === 'last' ? 'KEEP' : '';
    slot.append(badge, chips, digits);
    if (v.toldSuit) slot.classList.add('told-suit');
    if (v.toldRank) slot.classList.add('told-rank');

    const label = [
      `Your card ${v.slot + 1}`,
      v.known ? `known to be ${s.g.suits.find((x) => x.id === suitOf(v.known!))?.name} ${rankOf(v.known)}` : `could be ${v.suits.length} colours and ${v.ranks.length} numbers`,
      v.intent === 'play' ? 'the crew pointed at it to raise' : v.intent === 'save' ? 'the crew pointed at it to keep' : '',
      v.isChop ? 'next to be doused' : '',
    ].filter(Boolean);
    slot.setAttribute('aria-label', label.join(', '));

    slot.addEventListener('click', () => {
      this.deps.sfx('tap');
      this.deps.onSelect(picked ? { kind: 'none' } : { kind: 'mine', slot: v.slot });
    });

    /**
     * The one gesture in the game. Swipe DOWN to douse — pushing the card off the bottom of your
     * hand, which is what dousing looks like. Bound to dousing and nothing else, because a
     * mis-swipe costs a card and a spark comes back, whereas a mis-swiped RAISE could burn the last
     * haze and end the night on a gesture nobody meant to make.
     */
    if (s.you !== null && s.g.turn === s.you && !s.g.over) {
      this.drags.push(
        makeDraggable(slot, {
          onSwipe: (dir) => {
            if (dir !== 'down') return;
            const a = packDouse(s.you!, v.slot);
            if (legal(s.g, a)) {
              this.deps.sfx('douse');
              this.deps.submit(a);
            } else this.say('No spark to gain — the pool is already full.');
          },
        }),
      );
    }
    return slot;
  }

  // ── the bar, which is the only mutable place on the screen ─────────────────────────────────────

  private renderBar(): void {
    const s = this.state!;
    this.bar.replaceChildren();
    const yours = s.you !== null && s.g.turn === s.you && !s.g.over;

    if (s.g.over) {
      this.bar.append(el('span', 'barhint', 'The night is over.'));
      return;
    }
    if (s.pending) {
      this.bar.append(el('span', 'barhint', 'Sending…'));
      return;
    }
    if (!yours) {
      const who = s.names[s.g.turn] ?? 'The crew';
      this.bar.append(el('span', 'barhint', s.you === null ? 'Watching.' : `${who} is thinking…`));
      return;
    }

    if (s.sel.kind === 'mine') {
      const slot = s.sel.slot;
      this.bar.append(this.verb('Raise', 'raise', packRaise(s.you!, slot), 'Put it on its plume. Wrong card, and the sky hazes.'));
      this.bar.append(this.verb('Douse', 'douse', packDouse(s.you!, slot), 'Throw it away and take a spark back.'));
      this.bar.append(this.cancel());
      return;
    }

    if (s.sel.kind === 'mate' || s.sel.kind === 'aim') {
      const seat = s.sel.seat;
      const chosen = s.sel.kind === 'aim' ? s.sel : null;
      const picker = el('div', 'picker');
      for (const suit of s.g.suits) {
        const on = chosen?.hintKind === K_COLOUR && chosen.value === suit.id;
        const b = btn(`pick${on ? ' on' : ''}`, suit.glyph);
        b.style.setProperty('--suit', fillFor(suit.id, s.sixth));
        b.setAttribute('aria-label', `Point out ${suit.name}`);
        b.disabled = hintMask(s.g, seat, K_COLOUR, suit.id) === 0;
        b.addEventListener('click', () => {
          this.deps.sfx('tap');
          this.deps.onSelect({ kind: 'aim', seat, hintKind: K_COLOUR, value: suit.id });
        });
        picker.append(b);
      }
      for (let r = 1; r <= TOP; r++) {
        const on = chosen?.hintKind === K_RANK && chosen.value === r;
        const b = btn(`pick num${on ? ' on' : ''}`, String(r));
        b.setAttribute('aria-label', `Point out the ${r}s`);
        b.disabled = hintMask(s.g, seat, K_RANK, r) === 0;
        b.addEventListener('click', () => {
          this.deps.sfx('tap');
          this.deps.onSelect({ kind: 'aim', seat, hintKind: K_RANK, value: r });
        });
        picker.append(b);
      }
      this.bar.append(picker);
      if (chosen) {
        const mask = hintMask(s.g, seat, chosen.hintKind, chosen.value);
        this.bar.append(
          this.verb('Point', 'point', packPoint(s.you!, seat, chosen.hintKind, chosen.value, mask), 'Spend a spark and say it.'),
        );
      }
      this.bar.append(this.cancel());
      return;
    }

    this.bar.append(el('span', 'barhint', 'Tap one of your cards, or a crewmate to point something out.'));
  }

  private verb(label: string, cls: string, action: number, hint: string): HTMLElement {
    const s = this.state!;
    const b = btn(`verb ${cls}`, label);
    b.disabled = !legal(s.g, action);
    b.title = hint;
    b.addEventListener('click', () => {
      this.deps.sfx(cls);
      this.deps.submit(action);
    });
    return b;
  }

  private cancel(): HTMLElement {
    const b = btn('verb cancel', 'Back');
    b.addEventListener('click', () => this.deps.onSelect({ kind: 'none' }));
    return b;
  }

  destroy(): void {
    for (const d of this.drags) d.destroy();
    this.drags = [];
  }
}
